import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Phone, MapPin, Play, CheckCircle2, AlertTriangle, Eraser, Loader2, Check, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ChecklistList } from "@/components/ChecklistList";
import { EquipmentEditor, EquipmentReadList, toPayload } from "@/components/EquipmentLines";
import { EvidencePanel } from "@/components/EvidencePanel";
import { VmCameras } from "@/components/tech/VmCameras";
import { SignaturePad } from "@/components/SignaturePad";
import { BuDot } from "@/components/StatusBadge";
import { GpsFields } from "@/components/GpsFields";
import { GpsUnitsEditor } from "@/components/gps/GpsUnitsEditor";
import { hasLegacyGps } from "@/lib/gps";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fileUrl } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtSchedule } from "@/lib/format";
import { PrepSummary } from "@/components/tech/PrepSummary";
import { SiteMap } from "@/components/tech/SiteMap";
import { FindingsEditor } from "@/components/wo/FindingsEditor";
import { TechDataEditor } from "@/components/wo/TechDataEditor";
import { VehicleCard } from "@/components/wo/VehicleCard";
import { MaterialsChecklist } from "@/components/wo/MaterialsChecklist";
import { VvMaintenance } from "@/components/wo/VvMaintenance";
import { MainAcTerminals } from "@/components/wo/AcTerminals";
import { isVvMaint } from "@/lib/vvMaint";
import { TechComments } from "@/components/wo/TechComments";

const Block = ({ label, children }) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
    {children}
  </div>
);

const InfoRow = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <div className="text-[11px] text-muted-foreground">{label}</div>
    {value ? <div className="font-medium">{value}</div> : <div className="italic text-amber-300/90">No registrado en la OS</div>}
  </div>
);

export const StepInfo = ({ wo, setWo, canStart, onStart, starting }) => {
  const { meta } = useAuth();
  const c = wo.contact || {};
  return (
    <div className="space-y-3">
      <Block label="Cliente">
        <div className="font-display text-xl font-semibold" data-testid="tech-info-client">{wo.client_name}</div>
        {wo.project_name && <div className="text-sm text-muted-foreground">{wo.project_name}</div>}
        <div className="mt-3 space-y-2 border-t border-border pt-3 text-sm">
          {wo.site?.name && <InfoRow label="Sitio / sucursal" value={wo.site.name} testid="tech-info-site" />}
          <InfoRow label="Dirección del servicio" value={wo.service_address} testid="tech-info-address" />
        </div>
        <SiteMap wo={wo} />
        <div className="mt-3 space-y-2 text-sm">
          <InfoRow label="Contacto" value={c.name} testid="tech-info-contact" />
          <InfoRow label="Teléfono" value={c.phone} testid="tech-info-phone" />
        </div>
        {(c.phone || c.email) && <div className="mt-2 grid grid-cols-2 gap-2">
          {c.phone && <Button variant="outline" className="h-12" asChild data-testid="tech-call-button"><a href={`tel:${c.phone.replace(/[^\d+]/g, "")}`}><Phone className="mr-2 h-4 w-4" />Llamar</a></Button>}
          {c.email && <Button variant="outline" className="h-12" asChild data-testid="tech-email-button"><a href={`mailto:${c.email}`}><Mail className="mr-2 h-4 w-4" />Correo</a></Button>}
        </div>}
      </Block>
      <Block label="Servicio">
        <div className="flex items-center gap-2"><BuDot meta={meta} code={wo.business_unit_code} name={wo.business_unit_name} /> · <span>{wo.service_type_name}</span></div>
        {wo.technician_name && <div className="mt-1 text-sm" data-testid="tech-info-technician">Técnico asignado: {wo.technician_name}</div>}
        <div className="mt-1 font-mono text-xs text-muted-foreground">{fmtSchedule(wo.scheduled_date, wo.scheduled_time)}</div>
      </Block>
      <Block label="Alcance del trabajo"><p className="whitespace-pre-wrap text-sm">{wo.scope || "—"}</p></Block>
      {wo.notes && <Block label="Indicaciones de oficina"><p className="whitespace-pre-wrap text-sm">{wo.notes}</p></Block>}
      <PrepSummary woId={wo.id} />
      {isVvMaint(wo) && <Block label="Preparación · checklist de materiales y herramientas"><MaterialsChecklist wo={wo} setWo={setWo} editable={!["cerrada", "cancelada"].includes(wo.status)} /></Block>}
      {canStart && (
        <Button className="h-16 w-full text-lg font-semibold" onClick={onStart} disabled={starting} data-testid="tech-start-button">
          {starting ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Play className="mr-2 h-5 w-5" />} Iniciar servicio
        </Button>
      )}
    </div>
  );
};

export const StepChecklist = ({ wo, setWo, editable, phase }) => {
  const toggle = async (item, patch = { checked: !item.checked }) => {
    const optimistic = wo.checklist.items.map((i) => (i.id === item.id ? { ...i, ...patch } : i));
    setWo({ ...wo, checklist: { ...wo.checklist, items: optimistic } });
    try {
      const { data } = await api.patch(`/work-orders/${wo.id}/checklist/${item.id}`, patch);
      setWo((w) => ({ ...w, checklist: data }));
    } catch (e) {
      toast.error(errMsg(e));
      setWo((w) => ({ ...w, checklist: wo.checklist }));
    }
  };
  const items = phase ? wo.checklist.items.filter((i) => (i.phase || "final") === phase) : wo.checklist.items;
  if (phase === "inicial") return <InitialChecklist items={items} onPatch={toggle} disabled={!editable} />;
  return <ChecklistList items={items} onToggle={toggle} disabled={!editable} large tri={wo.checklist?.mode === "tri"} />;
};

const InitialItem = ({ i, onPatch, disabled }) => {
  const [notes, setNotes] = useState(i.notes || "");
  return (
    <div className="space-y-2 rounded-lg border border-border p-3" data-testid={`initial-item-${i.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">{i.label}</span>
        <div className="flex shrink-0 gap-1">
          <Button size="sm" variant={i.checked ? "default" : "outline"} disabled={disabled} onClick={() => onPatch(i, { checked: !i.checked })} data-testid={`initial-item-${i.id}-ok`}>Revisado</Button>
          <Button size="sm" variant={i.na ? "default" : "outline"} disabled={disabled} onClick={() => onPatch(i, { checked: false, na: !i.na })} data-testid={`initial-item-${i.id}-na`}>N/A</Button>
        </div>
      </div>
      <Input placeholder="Observación" value={notes} disabled={disabled} onChange={(e) => setNotes(e.target.value)}
        onBlur={() => notes !== (i.notes || "") && onPatch(i, { checked: !!i.checked, na: !!i.na, notes })} data-testid={`initial-item-${i.id}-notes`} />
    </div>
  );
};

const InitialChecklist = ({ items, onPatch, disabled }) => (
  <div className="space-y-2" data-testid="tech-initial-checklist">
    <p className="text-sm text-muted-foreground">Guía interna antes de iniciar la instalación (no aparece en el PDF). Si hay golpes o daños, regístralos con foto “Falla / hallazgo” en Equipos / Fotos.</p>
    {items.map((i) => <InitialItem key={i.id} i={i} onPatch={onPatch} disabled={disabled} />)}
  </div>
);

export const StepEquipment = ({ wo, setWo, editable }) => {
  const [lines, setLines] = useState(wo.equipment);
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(toPayload(lines)) !== JSON.stringify(toPayload(wo.equipment));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/work-orders/${wo.id}/equipment`, { lines: toPayload(lines) });
      setWo((w) => ({ ...w, equipment: data }));
      setLines(data);
      toast.success("Equipamiento guardado");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  if (wo.business_unit_code === "gps") return (
    <div className="space-y-4"><GpsStep wo={wo} setWo={setWo} editable={editable} />
      {lines.length > 0 && <div className="space-y-2" data-testid="tech-gps-equipment-history"><p className="text-sm font-medium">Equipamiento registrado</p><EquipmentReadList lines={lines} /></div>}
      <StepPhotos wo={wo} editable={editable} /></div>
  );
  return (
    <div className="space-y-3">
      {wo.business_unit_code === "videovigilancia_movil" && <div className="space-y-2 rounded-xl border border-border bg-card p-4"><div className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary">Vehículo</div><VehicleCard wo={wo} setWo={setWo} editable={editable} /></div>}
      <TechDataEditor wo={wo} editable={editable} onSaved={(td) => setWo((w) => ({ ...w, tech_data: td }))} />
      {isVvMaint(wo) && <VvMaintenance wo={wo} setWo={setWo} editable={editable} />}
      <MainAcTerminals wo={wo} setWo={setWo} editable={editable} />
      <p className="text-sm text-muted-foreground">Registra lo que realmente se instaló o entregó.</p>
      <EquipmentEditor lines={lines} onChange={setLines} disabled={!editable} buCode={wo.business_unit_code} allowManual workOrderId={wo.id} allowNotes />
      {editable && dirty && <Button className="h-14 w-full text-base" onClick={save} disabled={saving} data-testid="tech-equipment-save-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Guardar equipamiento</Button>}
    </div>
  );
};

const GpsStep = ({ wo, setWo, editable }) => {
  const g = wo.extensions?.gps || {};
  const onSaved = (units) => {
    setWo((w) => ({ ...w, extensions: { ...(w.extensions || {}), gps: { ...(w.extensions?.gps || {}), units } } }));
    api.get(`/work-orders/${wo.id}`).then((r) => setWo((w) => ({ ...w, checklist: r.data.checklist })));
  };
  return (
    <div className="space-y-3 rounded-xl border border-border bg-card p-4" data-testid="tech-gps-units">
      <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary">03B — Unidades GPS</div>
      {hasLegacyGps(g) && <LegacyGpsStep wo={wo} setWo={setWo} editable={editable} />}
      <GpsUnitsEditor woId={wo.id} gps={g} editable={editable} techMode onSaved={onSaved} serviceType={wo.service_type_code} />
      <TechDataEditor wo={wo} editable={editable} onSaved={(td) => setWo((w) => ({ ...w, tech_data: td }))} />
    </div>
  );
};

const LegacyGpsStep = ({ wo, setWo, editable }) => {
  const [gps, setGps] = useState(wo.extensions?.gps || { vehicle: {}, device: {} });
  const save = async () => {
    try {
      const { data } = await api.patch(`/work-orders/${wo.id}/gps`, gps);
      setWo((w) => ({ ...w, extensions: { ...(w.extensions || {}), gps: { ...(w.extensions?.gps || {}), ...data } } }));
      toast.success("Datos GPS guardados");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3 rounded-lg border border-dashed border-border p-3" data-testid="tech-gps-legacy">
      <p className="text-xs text-muted-foreground">Datos con el formato anterior (una unidad, sin plan).</p>
      <GpsFields value={gps} onChange={setGps} disabled={!editable} />
      {editable && <Button className="h-12 w-full" onClick={save} data-testid="tech-gps-save-button">Guardar vehículo y dispositivo</Button>}
    </div>
  );
};

export const StepPhotos = ({ wo, editable }) => {
  const [key, setKey] = useState(0);
  const vm = wo.business_unit_code === "videovigilancia_movil" && wo.service_type_code === "instalacion";
  return (
    <div className="space-y-3">
      {wo.origin === "MAINTENANCE" && wo.business_unit_code !== "videovigilancia_movil" && <p className="rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-sm text-sky-200" data-testid="tech-maint-photo-hint">OS de mantenimiento: registra fotos con categoría “Antes” y “Después”.</p>}
      {vm && <VmCameras wo={wo} editable={editable} onSaved={() => setKey((k) => k + 1)} />}
      <EvidencePanel key={key} woId={wo.id} editable={editable} addOnly={wo.status === "pendiente_cierre"} units={wo.business_unit_code === "gps" ? wo.extensions?.gps?.units || [] : []} />
    </div>
  );
};

export const StepNotes = ({ wo, setWo, editable }) => {
  const [text, setText] = useState(wo.technician_notes || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/work-orders/${wo.id}/technician-notes`, { technician_notes: text });
      setWo((w) => ({ ...w, technician_notes: text }));
      toast.success("Observaciones guardadas");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-3">
      <Textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} disabled={!editable} className="text-base" placeholder="Ej. Se dejó configurado acceso remoto en teléfono del administrador." data-testid="tech-notes-input" />
      {editable && <Button className="h-14 w-full text-base" onClick={save} disabled={saving || text === (wo.technician_notes || "")} data-testid="tech-notes-save-button">Guardar observaciones</Button>}
      {wo.origin === "MAINTENANCE" && (!isVvMaint(wo) || wo.findings?.length > 0) && <Block label="Hallazgos del mantenimiento"><FindingsEditor wo={wo} editable={editable} onSaved={(f) => setWo((w) => ({ ...w, findings: f }))} /></Block>}
      <TechComments wo={wo} setWo={setWo} canAdd={wo.status === "pendiente_cierre"} serviceId={null} />
    </div>
  );
};

export const StepSignature = ({ wo, setWo, editable }) => {
  const pad = useRef(null);
  const [name, setName] = useState(wo.signature?.receiver_name || wo.contact?.name || "");
  const [company, setCompany] = useState(wo.signature?.receiver_company || "");
  const [position, setPosition] = useState(wo.signature?.receiver_position || "");
  const [inked, setInked] = useState(false);
  const [redo, setRedo] = useState(!wo.signature);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/work-orders/${wo.id}/signature`, { receiver_name: name, receiver_company: company, receiver_position: position, image_data: pad.current.toDataURL() });
      setWo((w) => ({ ...w, signature: data }));
      setRedo(false);
      toast.success("Firma guardada");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  if (wo.signature && !redo)
    return (
      <div className="space-y-3" data-testid="tech-signature-saved">
        <img src={`${fileUrl(`/files/signature/${wo.id}`)}&t=${wo.signature.signed_at}`} alt="Firma" className="h-40 w-full rounded-lg bg-white object-contain p-2" />
        <div className="text-sm"><span className="font-medium">{wo.signature.receiver_name}</span>{wo.signature.receiver_company && ` · ${wo.signature.receiver_company}`}{wo.signature.receiver_position && ` · ${wo.signature.receiver_position}`} <span className="text-muted-foreground">· {fmtDateTime(wo.signature.signed_at)}</span></div>
        {editable && <Button variant="outline" className="h-12 w-full" onClick={() => setRedo(true)} data-testid="tech-signature-redo-button">Volver a firmar</Button>}
      </div>
    );
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nombre de quien recibe</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="h-12 text-base" disabled={!editable} data-testid="tech-receiver-name-input" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Empresa" className="h-12" disabled={!editable} data-testid="tech-receiver-company-input" />
        <Input value={position} onChange={(e) => setPosition(e.target.value)} placeholder="Cargo" className="h-12" disabled={!editable} data-testid="tech-receiver-position-input" />
      </div>
      <div className="rounded-xl border-2 border-dashed border-slate-700 p-2"><SignaturePad ref={pad} onInk={setInked} /></div>
      <p className="text-xs text-muted-foreground">Firma de conformidad de recepción del servicio.</p>
      {editable && (
        <div className="grid grid-cols-[auto_1fr] gap-2">
          <Button variant="outline" className="h-14" onClick={() => pad.current.clear()} data-testid="tech-signature-clear-button"><Eraser className="h-5 w-5" /></Button>
          <Button className="h-14 text-base" disabled={!inked || !name.trim() || saving} onClick={save} data-testid="tech-signature-save-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Guardar firma</Button>
        </div>
      )}
    </div>
  );
};

const SummaryRow = ({ ok, label }) => (
  <div className="flex items-center gap-2 text-sm">
    {ok ? <Check className="h-4 w-4 text-emerald-400" /> : <AlertTriangle className="h-4 w-4 text-amber-400" />}
    <span className={ok ? "" : "text-amber-200"}>{label}</span>
  </div>
);

export const StepClose = ({ wo, editable, onFinish, photoCount }) => {
  const { meta } = useAuth();
  const [mode, setMode] = useState(null);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [revisit, setRevisit] = useState(true);
  const [busy, setBusy] = useState(false);
  const items = wo.checklist.items;
  const reqMissing = items.filter((i) => i.required && !i.checked).length;
  const gpsWo = wo.business_unit_code === "gps";
  const gpsRemoval = gpsWo && wo.service_type_code === "desinstalacion";
  const gpsUnits = wo.extensions?.gps?.units || [];
  const removed = gpsUnits.filter((u) => u.target && u.removal?.condicion && u.removal?.destino).length;
  const removalOk = removed > 0 && removed === gpsUnits.length;
  useEffect(() => setMode(null), [wo.status]);

  const finish = async (result, signature_pending = false) => {
    setBusy(true);
    try {
      await onFinish({ result, pending_reason: reason, pending_notes: notes, needs_revisit: revisit, signature_pending });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2 rounded-xl border border-border bg-card p-4" data-testid="tech-close-summary">
        <SummaryRow ok={reqMissing === 0} label={reqMissing === 0 ? "Checklist obligatorio completo" : `${reqMissing} puntos obligatorios sin completar`} />
        {gpsRemoval ? <SummaryRow ok={removalOk} label={`${removed} equipo(s) GPS a retirar registrado(s)`} />
          : gpsWo && gpsUnits.length ? <SummaryRow ok label={`${gpsUnits.length} unidad(es) GPS guardada(s)`} />
          : <SummaryRow ok={wo.equipment.length > 0} label={`${wo.equipment.length} partidas de equipo`} />}
        <SummaryRow ok={photoCount > 0} label={`${photoCount} fotografías`} />
        <SummaryRow ok={!!wo.signature} label={wo.signature ? `Firmado por ${wo.signature.receiver_name}` : "Falta firma de quien recibe (puedes finalizar con firma pendiente)"} />
      </div>
      {editable && !mode && (
        <div className="grid gap-3">
          <Button className="h-16 bg-emerald-600 text-lg font-bold text-white hover:bg-emerald-500" disabled={busy || !wo.signature} onClick={() => finish("completada")} data-testid="tech-complete-button">
            <CheckCircle2 className="mr-2 h-6 w-6" />COMPLETADA
          </Button>
          {!wo.signature && (
            <Button variant="outline" className="h-14 border-emerald-600/60 text-base font-semibold" disabled={busy} onClick={() => finish("completada", true)} data-testid="tech-complete-pending-signature-button">
              Finalizar trabajo — firma pendiente
            </Button>
          )}
          <Button className="h-16 bg-orange-600 text-lg font-bold text-white hover:bg-orange-500" onClick={() => setMode("pending")} data-testid="tech-pending-button">
            <AlertTriangle className="mr-2 h-6 w-6" />PENDIENTE
          </Button>
          <Button variant="outline" className="h-14 border-emerald-600/60 text-base font-semibold" disabled={busy || !wo.signature} onClick={() => setMode("obs")} data-testid="tech-complete-obs-button">
            Completada con observaciones
          </Button>
        </div>
      )}
      {mode === "obs" && (
        <div className="space-y-3 rounded-xl border border-emerald-600/40 bg-emerald-500/5 p-4" data-testid="tech-obs-form">
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Observaciones *" className="text-base" data-testid="tech-obs-notes-input" />
          <div className="grid grid-cols-2 gap-2">
            <Button variant="ghost" className="h-14" onClick={() => setMode(null)}>Cancelar</Button>
            <Button className="h-14 bg-emerald-600 text-white hover:bg-emerald-500" disabled={busy || notes.trim().length < 3} onClick={() => finish("completada_obs")} data-testid="tech-obs-confirm-button">Confirmar</Button>
          </div>
        </div>
      )}
      {mode === "pending" && (
        <div className="space-y-4 rounded-xl border border-orange-500/40 bg-orange-500/5 p-4" data-testid="tech-pending-form">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Motivo *</Label>
            <div className="grid gap-2">
              {(meta?.pending_reasons || []).map((r) => (
                <button key={r} type="button" onClick={() => setReason(r)} data-testid={`tech-pending-reason-${r.slice(0, 12).replace(/\W+/g, "-").toLowerCase()}`}
                  className={`min-h-[48px] rounded-lg border px-3 text-left text-sm ${reason === r ? "border-orange-400 bg-orange-500/15" : "border-border"}`}>{r}</button>
              ))}
            </div>
          </div>
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Observación" className="text-base" data-testid="tech-pending-notes-input" />
          <label className="flex min-h-[48px] items-center justify-between text-sm">Requiere nueva visita<Switch checked={revisit} onCheckedChange={setRevisit} data-testid="tech-pending-revisit-switch" /></label>
          <div className="text-xs text-muted-foreground">Fotografía opcional:</div>
          <EvidencePanel woId={wo.id} editable defaultCategory="otra" />
          <div className="grid grid-cols-2 gap-2">
            <Button variant="ghost" className="h-14" onClick={() => setMode(null)}>Cancelar</Button>
            <Button className="h-14 bg-orange-600 text-white hover:bg-orange-500" disabled={!reason || busy} onClick={() => finish("pendiente")} data-testid="tech-pending-confirm-button">Confirmar pendiente</Button>
          </div>
        </div>
      )}
    </div>
  );
};
